import {useState} from 'react';
import React from 'react';

import {documentToHtmlString} from '@contentful/rich-text-html-renderer';
import Head from 'next/head';
import Image from 'next/image';
import {range} from 'ramda';
import {FormattedMessage, useIntl} from 'react-intl';
import styled, {css} from 'styled-components';

import {
  dimensions,
  colors,
  BorderOnlyActionButton,
  TrashIcon,
  Spinner,
} from '@stryd/react-ui';
import {ceilToMultiple} from '@stryd/util-lib';

import {regionConfigs} from 'src/config';
import {usePageRegionData} from 'src/contexts';
import {useCart} from 'src/features/cart/contexts';
import {LineItem, CartActionItem} from 'src/features/cart/types';
import {CartTestType} from 'src/features/cart/types';
import {getVariantPricing} from 'src/features/pricing/helpers';
import {usePriceFormatters} from 'src/hooks/usePriceFormatters';
import {useStripeCheckout} from 'src/hooks/useStripeCheckout';
import {colors as customColors} from 'src/styles/colors';
import {dimensions as customDimensions} from 'src/styles/dimensions';
import {getImageSourceFromAsset} from 'src/utils/contentful';

import {ErrorContainer} from '../ErrorContainer';
import {InternalLink} from '../InternalLink';
import {ProductVariantPrice} from '../ProductVariantPrice';
import {TermsOfService} from '../TermsOfService';

const {
  defaultPageVerticalPadding,
  defaultPageSidePadding,
  defaultPageVerticalPaddingMobile,
  defaultPageSidePaddingMobile,
} = customDimensions;

const CartPageContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  height: 100%;
  min-height: 100vh;
  width: 100%;

  padding: ${defaultPageVerticalPadding} ${defaultPageSidePadding};
  background-color: ${customColors.backgroundLight};

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    padding: ${defaultPageVerticalPaddingMobile} ${defaultPageSidePaddingMobile};
    align-items: flex-start;
  }
`;

const CartPageContentContainer = styled.div`
  max-width: 1250px;
  width: 100%;
`;

const CartPageHeader = styled.h1`
  font-size: 5rem;
  margin-bottom: 2.25rem;

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    font-size: 3rem;
  }
`;

const CartInfoContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;

  > * + * {
    margin-left: 66px;
  }

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    flex-direction: column;

    > * + * {
      margin-left: unset;
      margin-top: ${dimensions.twicePadding};
    }
  }
`;

const CartLineItemsContainer = styled.div`
  flex: 1;
  background-color: white;
  border-radius: ${dimensions.defaultBorderRadius};
  padding: calc(1.75 * ${dimensions.defaultPadding});

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    width: 100%;
    max-width: unset;
    margin-right: 0;
  }
`;

const CartSummaryContainer = styled.div`
  flex: 1;
  max-width: 350px;
  background: white;
  padding: calc(2.5 * ${dimensions.defaultPadding}) ${dimensions.twicePadding};
  border-radius: ${dimensions.defaultBorderRadius};

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    width: 100%;
    max-width: unset;
  }
`;

const CartLineItemRow = styled.div`
  position: relative;

  display: flex;

  padding: calc(1.75 * ${dimensions.defaultPadding}) 0;
  border-bottom: 1px solid ${customColors.border};

  &:first-of-type {
    padding-top: 0;
  }

  &:last-of-type {
    padding-bottom: 0;
    border-bottom: none;
  }

  > * + * {
    margin-left: ${dimensions.twicePadding};
  }
`;

const SummaryLineContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${dimensions.halfPadding};
`;

const titleTextStyle = css`
  font-size: 1.1rem;
  font-weight: normal;
  margin-bottom: 0;
`;

const SummaryLineLabel = styled.label`
  ${titleTextStyle}
`;

const SummaryLineValue = styled.span`
  ${titleTextStyle}
`;

const CartTotalContainer = styled.div`
  margin-top: calc(1.5 * ${dimensions.defaultPadding});
  margin-bottom: ${dimensions.halfPadding};
  border-top: 1px solid ${customColors.border};
  border-bottom: 1px solid ${customColors.border};
  padding: calc(1.5 * ${dimensions.defaultPadding}) 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CartItemThumbnailContainer = styled.div`
  position: relative;
  width: 230px;
  height: 230px;
  background-color: ${customColors.border};

  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    width: 175px;
    height: 175px;
  }

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    width: 130px;
    height: 130px;
  }

  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    width: 63px;
    height: 63px;
  }
`;

const CartItemDataContainer = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: ${dimensions.defaultPadding} 0;

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    padding: 0;
  }
`;

const CartItemInfoContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    flex-direction: column;
    margin-bottom: ${dimensions.defaultPadding};
  }
`;

const CartItemInformation = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 66%;
`;

const CartItemTitle = styled.h4`
  ${titleTextStyle}
  font-weight: bold;
  margin-bottom: 1em;
`;

const CartItemPrice = styled.span`
  ${titleTextStyle}
  text-align: right;

  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    text-align: left;
  }
`;

const CartItemDescription = styled.div`
  max-width: 350px;
  font-size: 0.9rem;
  margin-bottom: ${dimensions.halfPadding};
  opacity: 1;
  color: ${customColors.textDark};

  > p {
    margin-bottom: 5px;
  }
`;

const CartItemCommitmentDescription = styled(CartItemDescription)`
  color: ${customColors.textLight};
  font-size: 0.75rem;
`;

const CartItemQtySelectorsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-block;

  margin: 0 ${dimensions.halfPadding};
  border-bottom: 1px solid transparent;

  cursor: pointer;

  select {
    appearance: none;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: ${dimensions.halfPadding};
    min-width: 3rem;

    &:focus {
      outline: none;
    }
  }

  &:hover {
    border-color: ${customColors.border};
  }

  &:focus-within {
    border-color: ${colors.themes.dark.textHighlight};
  }
`;

const SelectArrowWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  pointer-events: none;
  padding: 0 ${dimensions.halfPadding};

  &:after {
    content: '';
    display: block;
    border-top: 2px solid;
    border-right: 2px solid;
    border-bottom: 2px solid transparent;
    border-left: 2px solid transparent;
    transform: translate(-2.5px, -2.5px) rotate(135deg);
    width: 8px;
    height: 8px;
  }
`;

const ButtonContainer = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
`;

// eslint-disable-next-line
// @ts-ignore
const CheckoutButton = styled(BorderOnlyActionButton)`
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background-color: ${colors.themes.dark.textHighlight};
    color: ${colors.themes.dark.headerText};
    border-color: ${colors.themes.dark.textHighlight};

    stop {
      stop-color: ${colors.themes.dark.headerText};
    }

    &:hover {
      box-shadow: unset;
    }
  }
`;

const ProductLink = styled(InternalLink)`
  &:hover {
    color: unset;
  }
`;

const RemoveIconButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => {
  const intl = useIntl();

  return (
    // eslint-disable-next-line
    // @ts-ignore
    <ButtonContainer
      aria-label={intl.formatMessage({
        id: 'actions.remove',
        defaultMessage: 'Remove',
      })}
      {...props}
    >
      <TrashIcon
        title={intl.formatMessage({
          id: 'actions.remove',
          defaultMessage: 'Remove',
        })}
        containerStyle={{
          width: '25px',
          height: '25px',
        }}
        svgStyle={{
          hover: {fill: colors.themes.dark.textHighlight},
        }}
      />
    </ButtonContainer>
  );
};

const CartSummary: React.FC = () => {
  const {region, locale} = usePageRegionData();
  const [
    {totalNumberOfItems, subtotal, hasSubscriptionProduct, cartTestType},
    dispatch,
  ] = useCart();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutErr, setCheckoutErr] = useState(false);
  const {checkoutWithStripe} = useStripeCheckout();

  const abortCheckout = (opts: {err: boolean} = {err: true}) => {
    setCheckoutLoading(false);
    setCheckoutErr(opts.err);
    dispatch({
      type: 'SET_CHECKOUT_SESSION_ID',
      checkoutSessionId: null,
      region,
    });
  };

  const checkout = async () => {
    if (checkoutLoading) return;
    setCheckoutErr(false);
    setCheckoutLoading(true);

    const error = await checkoutWithStripe();

    if (error) {
      abortCheckout();
    }
  };

  return (
    <CartSummaryContainer>
      <SummaryLineContainer data-testid="cart-subtotal-line">
        <SummaryLineLabel>
          <FormattedMessage
            id="CartPageTemplate.subTotalLabel"
            defaultMessage="Subtotal ({numberOfItems}):"
            values={{numberOfItems: totalNumberOfItems}}
          />
        </SummaryLineLabel>
        <SummaryLineValue>{formatOneTimePrice(subtotal)}</SummaryLineValue>
      </SummaryLineContainer>
      <SummaryLineContainer>
        <SummaryLineLabel>
          <FormattedMessage
            id="CartPageTemplate.taxLabel"
            defaultMessage="Tax:"
          />
        </SummaryLineLabel>
        <SummaryLineValue>
          *
          <FormattedMessage
            id="CartPageTemplate.taxValue"
            defaultMessage="TBD"
          />
        </SummaryLineValue>
      </SummaryLineContainer>
      <SummaryLineContainer>
        <SummaryLineLabel>
          <FormattedMessage
            id="CartPageTemplate.shippingLabel"
            defaultMessage="Shipping:"
          />
        </SummaryLineLabel>
        <SummaryLineValue>
          <FormattedMessage
            id="CartPageTemplate.shippingValue"
            defaultMessage="Free"
          />
        </SummaryLineValue>
      </SummaryLineContainer>
      <CartTotalContainer>
        <SummaryLineLabel style={{fontWeight: 'bold'}}>
          <FormattedMessage
            id="CartPageTemplate.estimatedTotalLabel"
            defaultMessage="Estimated Total:"
          />
        </SummaryLineLabel>
        <SummaryLineValue style={{fontWeight: 'bold'}}>
          {formatOneTimePrice(subtotal)}
        </SummaryLineValue>
      </CartTotalContainer>
      <p style={{fontSize: '0.9rem'}}>
        *
        <FormattedMessage
          id="CartPageTemplate.taxNote"
          defaultMessage="Tax finalized at Checkout"
        />
      </p>
      <CheckoutButton
        id="cart-checkout-button"
        data-includes-subscription={
          hasSubscriptionProduct || cartTestType === CartTestType.FreeTrial
        }
        disabled={checkoutLoading || totalNumberOfItems === 0}
        onClick={checkout}
        style={{width: '100%', marginTop: dimensions.twicePadding}}
      >
        {checkoutLoading ? (
          <>
            <span style={{marginRight: dimensions.halfPadding}}>
              <FormattedMessage
                id="actions.checkingOut"
                defaultMessage="Checking out..."
              />
            </span>
            <Spinner size={'1.25rem'} />
          </>
        ) : (
          <FormattedMessage id="actions.checkout" defaultMessage="Checkout" />
        )}
      </CheckoutButton>

      {checkoutErr ? (
        <ErrorContainer
          role="alert"
          style={{marginTop: dimensions.defaultPadding}}
        >
          <FormattedMessage
            id="CheckoutPageTemplate.checkoutErr"
            defaultMessage="There was a problem creating your checkout session. Please try again or contact us at support@stryd.com."
          />
        </ErrorContainer>
      ) : null}

      <TermsOfService style={{marginTop: dimensions.defaultPadding}} />
    </CartSummaryContainer>
  );
};

/**
 * @deprecated
 */
export const FreeTrialLineItem = () => {
  const [, dispatch] = useCart();
  const intl = useIntl();
  const membershipName = intl.formatMessage({
    id: 'membershipName.membershipAddon',
    defaultMessage: 'Membership Add-on',
  });
  const {region} = usePageRegionData();

  return (
    <CartLineItemRow>
      <CartItemThumbnailContainer>
        <Image
          src={
            'https://images.ctfassets.net/8iip9pij3rta/2xcKMrOHE2kE9CKINTSPzP/548a873c293a15a31b91d9e689a0893b/Group_1784.png'
          }
          alt={'Free Trial'}
          layout="fill"
          objectFit="contain"
        />
      </CartItemThumbnailContainer>
      <CartItemDataContainer>
        <CartItemInfoContainer>
          <CartItemInformation>
            <CartItemTitle>
              <FormattedMessage
                id="membership.trial.cart.title"
                values={{membershipName}}
              />
            </CartItemTitle>

            <CartItemDescription>
              <p>
                <FormattedMessage
                  id="membership.trial.cart.description"
                  values={{membershipName}}
                />
              </p>
            </CartItemDescription>
          </CartItemInformation>
        </CartItemInfoContainer>

        <CartItemQtySelectorsContainer style={{justifyContent: 'flex-end'}}>
          <RemoveIconButton
            onClick={() => {
              dispatch({
                type: 'UPDATE_CART_TEST_TYPE',
                testType: null,
                region,
              });
            }}
          />
        </CartItemQtySelectorsContainer>
      </CartItemDataContainer>
    </CartLineItemRow>
  );
};

const CartLineItems: React.FC = () => {
  const [{lineItems}, dispatch] = useCart();
  const {region, locale} = usePageRegionData();
  const {formatRecurringPrice: formatSubscriptionCommitment} =
    usePriceFormatters({locale, includeCommitment: true});

  const {currency} = regionConfigs[region];

  const handleItemQtyUpdate = (item: CartActionItem, newQty: number) => {
    dispatch({
      type: 'UPDATE_ITEM_QUANTITY',
      quantity: newQty,
      item,
      region,
    });
  };

  const handleItemRemoval = (item: CartActionItem) => {
    dispatch({
      type: 'REMOVE_ITEM_FROM_CART',
      item,
      region,
    });
  };

  const getItemQtyOptions = (item: LineItem) => {
    const {
      selectedVariant: {
        fields: {maxCheckoutQuantity},
      },
    } = item;

    const max = maxCheckoutQuantity
      ? maxCheckoutQuantity
      : item.quantity > 10
      ? ceilToMultiple(item.quantity, 5)
      : 10;

    return range(1, max + 1); // range is exclusive on right side
  };

  if (lineItems.length === 0) {
    return (
      <CartLineItemsContainer>
        <p style={{margin: 0}}>
          <FormattedMessage
            id="CartPageTemplate.emptyCartMessage"
            defaultMessage="You don't have any items in your cart."
          />
        </p>
      </CartLineItemsContainer>
    );
  }

  return (
    <CartLineItemsContainer>
      {lineItems.map((item) => {
        const {selectedVariant, product} = item;
        const thumbnail = selectedVariant.fields.previewImage;

        const {recurringPricesTotals} = getVariantPricing({
          variant: selectedVariant,
          region,
          currency,
        });
        const commitments = recurringPricesTotals.filter(
          (price) => price.min_recurring_intervals > 0
        );

        const name = selectedVariant.fields.fullName || product.fields.name;
        const description =
          selectedVariant.fields.cartDescription ||
          selectedVariant.fields.description ||
          product.fields.description;

        return (
          <CartLineItemRow
            key={selectedVariant.sys.id}
            data-testid={`line-item-${selectedVariant.sys.id}`}
          >
            <CartItemThumbnailContainer>
              <Image
                src={getImageSourceFromAsset(thumbnail)}
                alt={thumbnail.fields.title}
                layout="fill"
                objectFit="contain"
              />
            </CartItemThumbnailContainer>
            <CartItemDataContainer>
              <CartItemInfoContainer>
                <CartItemInformation>
                  <ProductLink
                    href={`/products/${product.fields.slug}/${selectedVariant.fields.slug}`}
                  >
                    <CartItemTitle>{name}</CartItemTitle>
                  </ProductLink>
                  {description && (
                    <CartItemDescription
                      dangerouslySetInnerHTML={{
                        __html: documentToHtmlString(description),
                      }}
                    />
                  )}

                  {commitments.length > 0 &&
                    commitments.map((commitment) => (
                      <CartItemCommitmentDescription
                        key={`${commitment.unit_amount}-${commitment.min_recurring_intervals}`}
                      >
                        {formatSubscriptionCommitment(commitment)}
                      </CartItemCommitmentDescription>
                    ))}
                </CartItemInformation>
                <CartItemInformation>
                  <CartItemPrice>
                    <ProductVariantPrice
                      quantity={item.quantity}
                      variant={selectedVariant}
                      intervalFormat={'short'}
                    />
                  </CartItemPrice>
                </CartItemInformation>
              </CartItemInfoContainer>

              <CartItemQtySelectorsContainer>
                <div style={{fontSize: '0.9rem'}}>
                  <label htmlFor={`qty-select-${selectedVariant.sys.id}`}>
                    <FormattedMessage
                      id="CartPageTemplate.quantityLabel"
                      defaultMessage="Quantity"
                    />
                  </label>

                  <SelectWrapper>
                    <select
                      id={`qty-select-${selectedVariant.sys.id}`}
                      value={item.quantity.toString()}
                      onChange={(e) =>
                        handleItemQtyUpdate(item, parseInt(e.target.value, 10))
                      }
                    >
                      {getItemQtyOptions(item).map((qty) => {
                        const qtyStr = qty.toString();
                        return (
                          <option key={qtyStr} value={qtyStr}>
                            {qtyStr}
                          </option>
                        );
                      })}
                    </select>
                    <SelectArrowWrapper />
                  </SelectWrapper>
                </div>

                <RemoveIconButton onClick={() => handleItemRemoval(item)} />
              </CartItemQtySelectorsContainer>
            </CartItemDataContainer>
          </CartLineItemRow>
        );
      })}

      {/* {cartTestType === CartTestType.FreeTrial && <FreeTrialLineItem />} */}
    </CartLineItemsContainer>
  );
};

export const CartPageTemplate: React.FC = () => {
  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      <CartPageContainer>
        <CartPageContentContainer>
          <CartPageHeader>
            <FormattedMessage
              id="CartPageTemplate.pageHeader"
              defaultMessage="Your Cart"
            />
          </CartPageHeader>
          <CartInfoContainer>
            <CartLineItems />
            <CartSummary />
          </CartInfoContainer>
        </CartPageContentContainer>
      </CartPageContainer>
    </>
  );
};
